import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { dataGridStyle } from '../../../Styles/VMStyles';
import {
  APPOINTMENTS,
  SESSION_USER_COUNTRY_DATA_KEY,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
} from '../../../Util/VMConstants';

import axios from 'axios';
import {
  API_GET_APPT_FOLLOWUPSList,
  API_ADD_UPDATE_APPT_FOLLOWUPS,
  API_DELETE_APPT_FOLLOWUPS,
} from '../../../Util/URLConstants';

import { toast } from 'react-toastify';
import Loader from '../../../Components/Loader';
import StorageFactory from '../../../Util/StorageFactory';
import {
  checkIsPermissionExist,
  displayFormattedDateTime,
  formatDateTimePickerForApi,
  parseDateTimeFromApiForPicker,
  renderSNo,
} from '../../../Util/UtilHelper';
import { canMutateRecord, RECORD_OWNERSHIP_DENIED_MESSAGE } from '../../../Util/recordAuthorization';

import StyledInputField from '../../../Components/Custom/StyledInputField';
import MuiTypographyMandatory from '../../../Components/Custom/MuiTypographyMandatory';
import CreatedByCell from '../../../Components/Custom/CreatedByCell';

const AddApptFollowUps = ({ customer, type, readOnly = false }) => {
  /* ---------------- STATE ---------------- */
  const [formState, setFormState] = useState({
    date_time: null,
    notes: '',
    editId: null,
  });

  const [items, setItems] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  // delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  /* ---------------- SESSION ---------------- */
  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);

  const canUpdate = checkIsPermissionExist(VM_PERMISSIONS, 'VM011');
  const canDelete = checkIsPermissionExist(VM_PERMISSIONS, 'VM012');

  const currencySymbol =
    sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';

  /* ---------------- FETCH DATA ---------------- */
  const fetchData = async (pageModel = paginationModel) => {
    try {
      setLoading(true);

      const response = await axios.get(API_GET_APPT_FOLLOWUPSList, {
        params: {
          enquiry_id: customer?.appointment_id,
          type,
          page: pageModel.page + 1, // backend is 1-based
          pageSize: pageModel.pageSize,
        },
      });

      const respData = response.data || {};

      if (respData.status_code === 200) {
        // Normalize rows: ensure a unique `id` and a page-aware `s_no` for renderSNo()
        const list = (respData.data || []).map((item, idx) => ({
          ...item,
          s_no: idx + 1 + (pageModel.page || 0) * (pageModel.pageSize || 0),
          id: item.id || item.followup_id || item.enquiry_id || `row-${(pageModel.page || 0) * (pageModel.pageSize || 0) + idx + 1}`,
        }));

        setItems(list);
        setRowCount(respData.pagination?.total || 0);
      } else {
        toast.error(respData.message || 'Failed to fetch data.');
      }
    } catch (error) {
      toast.error('Something went wrong while loading data.');
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- EFFECTS ---------------- */
  useEffect(() => {
    if (customer?.appointment_id) {
      fetchData(paginationModel);
    } else {
      setItems([]);
      setRowCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.appointment_id, type, paginationModel]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [customer?.appointment_id, type]);

  /* ---------------- FORM HANDLERS ---------------- */
  const handleChange = (field, value) => {
    if (field === 'date_time') {
      value = value ? dayjs(value) : null;
    }
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormState({ date_time: null, notes: '', editId: null });
    setShowForm(false);
  };

  const handleAddOrUpdate = async () => {
    const { date_time, notes, editId } = formState;

    if (!date_time || !notes.trim()) {
      toast.warning('Please enter mandatory fields.');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        type,
        date_time: formatDateTimePickerForApi(date_time),
        notes: notes.trim(),
        id: editId || undefined,
        user_id: userID,
        enquiry_id: customer.appointment_id,
      };

      await axios.post(API_ADD_UPDATE_APPT_FOLLOWUPS, payload);
      toast.success(editId ? 'Updated successfully.' : 'Added successfully.');

      fetchData();
      resetForm();
    } catch (error) {
      toast.error('Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- EDIT / DELETE ---------------- */
  const handleEdit = (id) => {
    const item = items.find((i) => Number(i.id) === Number(id));
    if (!item) return;
    if (!canMutateRecord(VM_PERMISSIONS, 'VM011', item)) {
      toast.error(RECORD_OWNERSHIP_DENIED_MESSAGE);
      return;
    }

    setFormState({
      date_time: parseDateTimeFromApiForPicker(item.date_time),
      notes: item.notes ?? '',
      editId: Number(id),
    });
    setShowForm(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    const item = items.find((i) => Number(i.id) === Number(deleteId));
    if (item && !canMutateRecord(VM_PERMISSIONS, 'VM012', item)) {
      toast.error(RECORD_OWNERSHIP_DENIED_MESSAGE);
      setDeleteDialogOpen(false);
      setDeleteId(null);
      return;
    }

    try {
      setLoading(true);
      await axios.get(
        `${API_DELETE_APPT_FOLLOWUPS}?type=${type}&id=${deleteId}`
      );
      toast.success('Deleted successfully.');
      fetchData();
    } catch {
      toast.error('Failed to delete.');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  /* ---------------- COLUMNS ---------------- */
  const columns = [
    renderSNo(),
    {
      field: 'date_time',
      headerName: 'Date & Time',
      flex: 2,
      renderCell: (params) =>
        displayFormattedDateTime(params.value, currencySymbol, { wallClock: true }),
    },
    { field: 'notes', headerName: 'Notes', flex: 2 },

    {
      field: "created_by",
      headerName: "Created By",
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        // params.value is expected to be an object
        const userObj = params.value || params.row?.created_by || null;
        return (userObj ? <CreatedByCell user={userObj} /> : <span>-</span>);
      },
    },
  ];

  if (canUpdate || canDelete) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => [
        !readOnly && canMutateRecord(VM_PERMISSIONS, 'VM011', params.row) && (
          <GridActionsCellItem
            key="edit"
            icon={<Tooltip title="Edit"><EditIcon color="primary" /></Tooltip>}
            label="Edit"
            onClick={() => handleEdit(params.id)}
          />
        ),
        !readOnly && canMutateRecord(VM_PERMISSIONS, 'VM012', params.row) && (
          <GridActionsCellItem
            key="delete"
            icon={<Tooltip title="Delete"><DeleteIcon color="error" /></Tooltip>}
            label="Delete"
            onClick={() => handleDeleteClick(params.id)}
          />
        ),
      ].filter(Boolean),
    });
  }

  /* ---------------- RENDER ---------------- */
  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
      {loading && <Loader loading={loading} />}

      {!readOnly && !showForm && checkIsPermissionExist(VM_PERMISSIONS, 'VM009') && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            startIcon={<AddCircleIcon />}
            variant="contained"
            onClick={() => setShowForm(true)}
          >
            Add {type === APPOINTMENTS ? 'Appointment' : 'Follow-up'}
          </Button>
        </Box>
      )}

      {showForm ? (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <MuiTypographyMandatory
            label={
              type === APPOINTMENTS
                ? 'Appointment Date & Time'
                : 'Follow-up Date & Time'
            }
            required
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              value={formState.date_time}
              onChange={(val) => handleChange('date_time', val)}
              sx={{ width: '100%', mb: 2 }}
              format="DD/MM/YYYY hh:mm A"
            />
          </LocalizationProvider>

          <StyledInputField
            heading="Notes"
            value={formState.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            multiline
            minRows={4}
            required
          />

          <Stack direction="row" spacing={2} mt={2}>
            <Button fullWidth variant="contained" onClick={handleAddOrUpdate}>
              {formState.editId ? 'Update' : 'Submit'}
            </Button>
            <Button variant="outlined" onClick={resetForm}>
              Cancel
            </Button>
          </Stack>
        </Paper>
      ) : (
        <DataGrid
          rows={items}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'} 
          sx={dataGridStyle}
        />
      )}

      {/* -------- DELETE CONFIRMATION DIALOG -------- */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to proceed with deleting this{' '}
            {type === APPOINTMENTS ? 'appointment' : 'follow-up'}?
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddApptFollowUps;
